// fx 'stickerShape' — the sticker's SOLID PLATE (die-cut backing) and its own
// bevel, as a live filter step.
//
// THE PLATE MODEL (owner 2026-07-20, model revision): "by default a sticker
// always covers the entirety of the back side of the text, it is not a border.
// The width of the 'border' only defines how much space there is between the
// edges of the sticker and the boundaries of the text." So a sticker is a
// SOLID PLATE drawn UNDER the letters in EVERY mode — never a glyph-hugging
// ring. `border` is the MARGIN (text edge → plate edge). The letters composite
// OVER the plate keeping everything the chain already did to them (bevel
// shading, contact shadow — which now lands ON the plate, the two-layer
// grounding).
//
//   shape 'outline' ("Match text" in the def) → the FILLED dilated glyph
//     silhouette: the stroke-suppressed glyph mask, dilated OUTWARD by the
//     margin, die-cut-closed (bridgeDiecutPockets — pockets between letters
//     are part of the silhouette law, exactly the baked border path), filled
//     with the `fill` FillValue. A die-cut sticker shaped like the words.
//   shape circle|rectangle|square|rounded → an analytic badge sized to the
//     INK BOX (measured from the glyph-only variant) padded by the margin,
//     grown per-axis by padWidth/padHeight (square→rectangle, circle→ELLIPSE
//     when the two differ), filled with the same FillValue.
//
// BEVEL EDGE LAW (owner 2026-07-20: "sticker bevels should always apply only to
// the edges of the sticker — never to the edges of the text"): a filled plate
// has ONLY outer edges, so `beveled` domes the plate's OWN alpha directly
// (shadeBevelCore with mask = the plate silhouette) — rims rise at the die-cut
// edge and read flat where the plate meets the letters, matching how the baked
// border layer has always shaded its filled fill+stroke blob. No inner-edge
// masking is needed; the height-source≠paint-mask contract exists for the
// general case but the plate passes mask === height source.
//
// FINISH (owner 2026-07-20): `finish` (matte|satin|glossy|metal) threads into
// the dome's shadeBevelCore call via domeParams — the SAME surface family the
// standard bevel exposes (fxBevel FINISHES, anchored to the baked style
// bundles). satin = the pre-finish dome constants (byte-stable default).
//
// EDGE / BORDER TOOL (owner 2026-07-20: "if we want borders on the sticker, we
// can use the border tool"): optional `edge {color, width}` paints an
// inside-aligned contour band following the plate's own outer silhouette (the
// die-cut edge), domed together with the fill when beveled. Absent = no edge.
//
// RENDERING / DEGRADE SPLIT: when this step is live the seam suppresses the
// object's native stroke in the BASE render (fxRender: a stickerShape step ⇒
// strokeWidth parked at 0, base die-cut closing skipped) so the plate is the
// SOLE sticker paint — no double-paint with the legacy halo ring. The sticker
// def's derive still emits ring stroke props purely as the stock-fabric DEGRADE
// approximation (recipe = portable approximation; live = plate). Saved pre-lane
// stickers therefore render as plates now — the owner ruled the plate IS the
// correct look (intended migration).
//
// PATCH COHERENCE: badge geometry is GLOBAL (the ink box) but region bakes see
// only a slice — full renders REMEMBER the measured ink box and the dome max-
// distance in RECIPE units (WeakMap, no instance props); region renders REUSE
// them. Match-text region bakes rebuild the dilated plate from the region's
// own variant slice (dilation is local — the spill margin guards the reach).
//
// TEXTURES: `fill` may be {type:'texture', id, scale} — an ID, never a URL
// (the portability law; the URL re-derives here via fillPresets). Tile density
// mirrors the engine's law: one tile pixel ≈ one print pixel at "Pattern size
// 100%" (72/300 print base — TEXTURE_PRINT_BASE_SCALE, restated here to keep
// this module editor-free).
//
// 3D: fxExtrude declares consumes:['stickerShape'] — a live 3D bake owns the
// raster (the extruded "Sticker plate" is the 3D-native answer); the step
// returns untouched when 3D drops to 0.
//
// v3 (Depth-Everywhere lane N1, 2026-07-22) — the sticker grows up:
//
// EXACT PLATES (raster fallback kept): where the object's silhouette exists
// as CONTOURS — text (the union letterform the contour machinery extracts
// via the seam's 'filledSolid' variant), shapes/icons (the U1 silhouette
// variant through the same tracer) — the plate OUTLINE is now cut as exact
// vector rings: contours → exactOffset.offsetRings(+margin, round join)
// (outward offset auto-bridges sub-2·margin letter gaps exactly like the
// raster dilation) → a morphological CLOSING at the diecutClose radius law
// (min(margin·0.4, 6u), mirrored here in ring space so exact and raster
// plates agree on which pockets a physical cutter seals). Analytic badges
// polygonize their geometry (chord error ≤ 0.25 output px) into the same
// ring pipeline. The rings feed the SAME fill/finish/dome raster pipeline —
// only the CONTOUR source upgrades, so the margin law now holds sub-pixel.
// The chamfer-dilation path remains the documented AUTO fallback: no
// contours (blank text, photo kind, region bake with no remembered
// contours), wasm unavailable, or any exact-path hiccup — logged once,
// never a failure. applySync (per-keystroke) cannot await wasm: it reuses
// the REMEMBERED rings when params+silhouette still match, else paints the
// raster plate; the debounced async pass lands the exact plate.
//
// edgeStyle 'smooth' | 'scallop': scallop = the bulletin-board die-cut —
// exactOffset.scallopEdge stamps discs ON the plate boundary (n = round(
// perimeter/spacing) per ring, actual spacing perimeter/n so the pattern
// CLOSES seamlessly — F1's count law). One knob: scallopSize drives the
// disc radius, MARGIN-proportional like a real bulletin border
// ((0.10 + 0.008·size)·margin + 0.5u, floor 1.2u); spacing = 1.7·radius
// keeps bumps continuous (spacing < 2r). The edge ring renderer (the
// border tool band) follows the scalloped contour for free — it reads the
// painted alpha. Scallop requires rings (exact path only); the raster
// fallback renders smooth (documented degrade).
//
// body 'flat' | 'puffy': puffy = the plate becomes a REAL 3D slab —
// lib/geo/puffySolid.buildPuffySolid(plateRings, {height: (1+0.14·
// puffDepth)·u, edgeRound: 0.45·height, flatBack, engine:'auto'}) baked
// through the house one-shot renderer (renderer3d.bakeScene) with a
// straight-on ORTHOGRAPHIC camera whose bounds are the plate rect in object
// units — registration is linear algebra, no perspective, so the slab lands
// exactly where the flat plate paints and region/patch bakes are camera
// crops of the same solid. Material: basic MeshStandardMaterial carrying
// the plate fill (solid color, or the manifest tile planar-projected at the
// 2D pattern density; gradients fall to their first stop — documented);
// volumeMaterials presets arrive in a later lane — `bodyMaterial` is
// accepted + stored as a STUB id for them. Lit by the house sticker sun
// (az 135, the bevel altitude).
//   PRECEDENCE (enforced in sanitize, honest): beveled + puffy are
//   EXCLUSIVE — the puffy body IS the 3D bevel, so body:'puffy' forces
//   beveled:false in the sanitized params (the JSON says what renders).
//   LETTERS-ONLY BEVEL LAW unchanged: the text's own bevel step domes the
//   letters, never the sticker — puffy shades only the plate layer.
//   INTERIM + DEGRADE: applySync (and any bake failure) paints the flat
//   plate WITH the dome as the puffy stand-in; at renderCaps materialTier
//   'floor' puffy DEGRADES to that flat dome permanently (logged once) —
//   the SwiftShader-removal posture, no WebGL asked for at all.
//   FENCES: the seam never runs this bake mid-edit (fxRender's WebGL fence
//   covers puffy sticker stacks via stickerWantsGlBake) and never in the
//   sync export chain (fxPrimeExport pre-bakes puffy stacks; the export
//   render draws the primed stash — same inherit as source steps).
//
// fxExtrude's plate (sticker under 3D) keeps its OWN path untouched: it
// consumes this step whole and builds its slab in plateGeometry — the
// exact-contour upgrade here applies to the 2D sticker render only.

import { registerFx } from './fxRegistry';
import { shadeBevelCore, BEVEL_ALTITUDE_DEG } from './fxBevel';
import { textureUrl } from '../../inspector/text/fillPresets';
import { bridgeDiecutPockets, diecutCloseRadiusPx } from '../raster/diecutClose.js';
// Union-contour extraction (sub-pixel, cached per object) — the same tracer
// U3 extrude uses for icons; text rides the seam's 'filledSolid' variant.
// Already in the editor's eager graph via fxRender — boot-neutral.
import { traceObjectSilhouette } from './fxContourOutline.js';
import { capsMaterialTier, capsTriBudget } from '../renderCaps.js';
// Kind routing for the v3 contour source (shapes = exact vectors via
// lib/geo/shapeRings; text/icons = traced). Pure data module, boot-safe.
import { objectKindOf } from './capabilityMatrix.js';
// BOOT LAW (manifoldCore): nothing in the editor's eager graph may import
// lib/geo statically — exactOffset/puffySolid (and three) load via dynamic
// import INSIDE applyAsync only (the geo chunk stays off the boot path;
// wasm itself is a second lazy hop behind getManifold).

// Print-sharpness base for texture tiles — MUST match useCanvasEditor's
// TEXTURE_PRINT_BASE_SCALE (72dpi workspace, 300dpi print).
const TEXTURE_PRINT_BASE_SCALE = 72 / 300;

const SHAPES = new Set(['outline', 'circle', 'rectangle', 'square', 'rounded']);
const FINISH_SET = new Set(['matte', 'satin', 'glossy', 'metal']);

// The plate's own sun — fxBevel's house default (azimuth 135, hidden altitude),
// so letters-bevel and plate-bevel agree without the step growing light params.
const LIGHT = { azDeg: 135, altDeg: BEVEL_ALTITUDE_DEG };

const clamp = (v, min, max, fb) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fb;
  return Math.min(max, Math.max(min, n));
};

function hexRGB(hex) {
  const h = String(hex || '#000000').replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.padEnd(6, '0');
  const n = parseInt(f.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Optional plate edge (border tool). width 0 / bad shape → null so a plate
 * with no edge omits the key and stays byte-stable. */
function sanitizeEdge(e) {
  if (!e || typeof e !== 'object') return null;
  const width = clamp(e.width, 0, 100, 0);
  if (!(width > 0)) return null;
  return { color: typeof e.color === 'string' ? e.color : '#000000', width };
}

function sanitize(p) {
  const src = p || {};
  // BODY first — beveled reads it (the puffy-vs-beveled precedence below).
  const body = src.body === 'puffy' ? 'puffy' : 'flat';
  // Fixed key order — the seam hashes steps with JSON.stringify. v3 keys
  // APPEND after `edge` so every pre-lane params object keeps its exact
  // byte prefix (stored JSON is never rewritten by this change — only a
  // user gesture re-writes params, and then the new keys ride along).
  return {
    shape: SHAPES.has(src.shape) ? src.shape : 'outline',
    // border = the MARGIN (text edge → plate edge), NOT a ring thickness.
    border: clamp(src.border, 1, 200, 45),
    cornerRadius: clamp(src.cornerRadius, 0, 60, 18),
    // Wider / Taller — extra per-axis margin ON TOP of border (shapes only;
    // meaningless for the glyph-hugging match-text plate). Default 0 = the
    // border-only fit. u-relative like border (× 0.3 · u).
    padWidth: clamp(src.padWidth, 0, 250, 0),
    padHeight: clamp(src.padHeight, 0, 250, 0),
    // PRECEDENCE (v3, honest and in the DATA): puffy IS the 3D bevel — a
    // puffy body forces beveled:false so the JSON says what renders. Flip
    // body back to 'flat' and beveled starts false (one explicit re-toggle,
    // never a silently remembered dome under a slab).
    beveled: body === 'puffy' ? false : !!src.beveled,
    // Surface family for the plate dome (see fxBevel FINISHES). satin = the
    // pre-finish dome constants (byte-stable default).
    finish: FINISH_SET.has(src.finish) ? src.finish : 'satin',
    // FillValue passthrough (string | {solid} | {*-gradient} | {texture}).
    fill: src.fill == null ? '#ffffff' : src.fill,
    // Plate edge contour (border tool) — null unless asked for.
    edge: sanitizeEdge(src.edge),
    // v3 — die-cut edge style: 'smooth' (today's law) | 'scallop' (the
    // bulletin-board border, exact path only — see the header).
    edgeStyle: src.edgeStyle === 'scallop' ? 'scallop' : 'smooth',
    // One scallop knob: drives disc radius (and spacing at 1.7·r).
    scallopSize: clamp(src.scallopSize, 4, 100, 50),
    // v3 — plate body: 'flat' (2D fill + optional dome) | 'puffy' (real 3D
    // slab, see the header for the bake + degrade laws).
    body,
    // Puffiness → slab height ((1 + 0.14·puffDepth)·u world units).
    puffDepth: clamp(src.puffDepth, 1, 100, 40),
    // STUB (documented): volumeMaterials preset id for the puffy body —
    // accepted + stored now, resolved by the volume-materials lane later.
    // Today the body always wears the basic plate-fill material.
    bodyMaterial: typeof src.bodyMaterial === 'string' && src.bodyMaterial
      ? src.bodyMaterial : null,
  };
}

// The plate always draws when the step exists (the step is created only for an
// active sticker — see syncShapeFxStep). A plate is never a visual no-op: even
// margin 0 is a glyph-shaped backing behind the text.
const isEmpty = () => false;

/** The margin (object units): text edge → plate edge. Same formula the halo's
 * strokeWidth used, so "Border" keeps meaning "sticker mass" across the
 * migration and a mode switch keeps the visual weight. */
const padU = (p, u) => Math.max(2, p.border * 0.3 * u);
/** Extra per-axis margin (Wider / Taller), same u-scale as border. */
const extraXU = (p, u) => Math.max(0, p.padWidth) * 0.3 * u;
const extraYU = (p, u) => Math.max(0, p.padHeight) * 0.3 * u;

// ------------------------------------------------------------ v3 mappings
/** Scallop disc radius (object units) — the one-knob law: bumps scale with
 * the MARGIN (a real bulletin-board border's scallops grow with its width):
 * radius = (0.10 + 0.008·scallopSize)·margin + 0.5u, floored at 1.2u.
 * size 50 ≈ half-margin bumps, 100 ≈ full-margin, 4 ≈ fine dots. Bumps
 * protrude OUTWARD by exactly this radius (spill reads it). */
const scallopRadiusU = (p, u) => Math.max(
  1.2 * u,
  (0.10 + 0.008 * p.scallopSize) * padU(p, u) + 0.5 * u,
);
/** Center-to-center spacing = 1.7·radius: < 2r, so neighbouring discs
 * overlap into the continuous bulletin-board scallop (never separated
 * dots); scallopEdge rounds the count to close each ring seamlessly. */
const SCALLOP_SPACING_K = 1.7;
/** Puffy slab TOTAL height (object units): puffDepth 1-100 → (1+0.14·d)·u.
 * Rim roundover = 0.45·height (buildPuffySolid clamps to its own laws and
 * auto-shrinks via the thin-stroke guard). */
const puffHeightU = (p, u) => (1 + 0.14 * p.puffDepth) * u;
const PUFF_EDGE_ROUND_K = 0.45;
/** True when this render should ATTEMPT the 3D puffy bake: body puffy AND
 * the device tier can hold real materials. The floor tier degrades to the
 * flat dome (honest, logged once at the render site). */
function puffyBakeWanted(p) {
  return p.body === 'puffy' && capsMaterialTier() !== 'floor';
}

/**
 * Seam-facing predicate (fxRender imports this — no cycle: this module
 * never imports fxRender): true when a step stack contains a NON-EMPTY
 * stickerShape step that will hit WebGL in applyAsync (body puffy at full
 * material tier). Gates three seam inherits exactly like source steps:
 * the mid-edit WebGL fence, fxPrimeExport's pre-bake, and the export
 * render's primed-stash draw. Raw step params in, own sanitize applied —
 * the same posture the seam takes for source steps.
 */
export function stickerWantsGlBake(steps) {
  if (!Array.isArray(steps)) return false;
  for (const step of steps) {
    if (!step || step.id !== 'stickerShape') continue;
    try {
      const p = sanitize(step.params || {});
      if (!isEmpty(p) && puffyBakeWanted(p)) return true;
    } catch (_) { /* malformed step — never fences */ }
  }
  return false;
}

/** Dome-shading params for the plate layer: the border (margin) drives the
 * shoulder from the outer edge inward; fixed pleasant depth/soften; the
 * finish selects the surface. One-click, not a second bevel panel. */
function domeParams(p) {
  return {
    depth: 55,
    size: p.border,
    soften: 20,
    angle: LIGHT.azDeg,
    highlight: 85,
    shadow: 62,
    technique: 'smooth',
    lightMode: 'custom',
    finish: p.finish, // threads into shadeBevelCore's FINISHES selection
  };
}

/** Scalar spill = the patch-bake neighborhood guard. Un-beveled ANALYTIC
 * badges are pure fills (no neighborhood); the match-text plate's dilation +
 * die-cut closing read ∝ the margin; the dome reads ∝ the border. v3: the
 * scalloped edge protrudes a further disc radius outward; a puffy body adds
 * only AA slack (the slab's straight-on footprint IS the plate silhouette —
 * shading lives inside it, and patch bakes are exact camera crops needing
 * no raster neighborhood). The interim/degrade flat dome under puffy reads
 * the dome neighborhood, so the dome term also arms for puffy. */
function spill(p, u) {
  const domed = p.beveled || p.body === 'puffy';
  const dome = domed ? (9 + 0.26 * p.border) * u + 2 : 0;
  const scallop = p.edgeStyle === 'scallop' ? scallopRadiusU(p, u) + u : 0;
  const puffy = p.body === 'puffy' ? 2 * u : 0;
  if (p.shape === 'outline') return padU(p, u) + 2 * u + dome + scallop + puffy;
  return dome + scallop + puffy;
}

/** Anisotropic cache margins (object units per side) — the plate paints far
 * outside the glyph tight box. Honest over-estimate from the TIGHT box.
 * v3: scallop bumps + puffy AA ride every mode's pad. */
function spillXY(p, { u, w, h }) {
  let pad = padU(p, u) + 2 * u; // base margin + AA/upscale slack
  if (p.edgeStyle === 'scallop') pad += scallopRadiusU(p, u) + u;
  if (p.body === 'puffy') pad += 2 * u;
  if (p.shape === 'outline') {
    // Match-text plate = glyphs dilated by the margin → `pad` beyond the tight
    // box on every side (isotropic; padWidth/padHeight don't apply here).
    return { marginX: pad, marginY: pad };
  }
  const padX = pad + extraXU(p, u);
  const padY = pad + extraYU(p, u);
  if (p.shape === 'circle') {
    const r = Math.hypot(w, h) / 2;
    return { marginX: Math.max(0, r + padX - w / 2), marginY: Math.max(0, r + padY - h / 2) };
  }
  if (p.shape === 'square') {
    const base = Math.max(w, h) / 2;
    return { marginX: Math.max(0, base + padX - w / 2), marginY: Math.max(0, base + padY - h / 2) };
  }
  return { marginX: padX, marginY: padY }; // rectangle | rounded
}

// ---------------------------------------------------------------- memory
// Full renders measure; region renders reuse. Values in RECIPE units.
const MEM = new WeakMap(); // obj → { ink:{x0,y0,x1,y1}, plateDistU }

function memOf(obj) {
  let m = obj ? MEM.get(obj) : null;
  if (!m && obj) { m = {}; MEM.set(obj, m); }
  return m || {};
}

const loggedOnce = new Set();
function logOnce(key) {
  if (loggedOnce.has(key)) return;
  loggedOnce.add(key);
  console.warn(`[fx stickerShape] ${key}`);
}

// v3 probe surface (window.__cceFx.sticker via fxRender's dev block):
// exact-vs-raster routing truth + the puffy bake ledger.
const stats = {
  exactPlates: 0, // renders painted from exact rings (built or reused)
  rasterFallbacks: 0, // renders that WANTED rings but fell to the raster path
  ringBuilds: 0, // wasm ring builds (misses — reuse costs nothing)
  scallopPlates: 0, // ring builds that ran scallopEdge
  lastRingCount: 0,
  puffyBakes: 0, // GL slab bakes completed
  puffyDegrades: 0, // puffy renders served by the flat dome (floor tier / bake failure)
  lastPuffyMs: 0,
  lastPuffyEngine: null, // buildPuffySolid stats.engine of the last solid build
};

/** Dev/probe surface — exposed via window.__cceFx.sticker (fxRender). */
export function getStickerShapeStats() {
  return { ...stats };
}

// ------------------------------------------------- v3 exact-plate machinery
// MEM gains two v3 slots (full renders build, sync/region renders reuse):
//   rings     — { pkey, sig, src, rings, bounds }: the FINAL plate rings
//               (margin offset + die-cut closing + scallop), object units.
//               pkey = ring-shaping params + u + the closing-threshold flag;
//               sig = the cheap silhouette signature (sync reuse gate);
//               src = the source-contour array IDENTITY (async reuse gate —
//               fxContourOutline memoizes, so an unchanged silhouette hands
//               back the same array object).
//   puffyMesh — { key, mesh }: buildPuffySolid's typed arrays (the expensive
//               half — minkowski/levelset). The GL bake re-runs per rect/
//               scale (cheap, ortho crop); the SOLID rebuilds only when the
//               rings or the puff params move.

/** Cheap silhouette signature for SYNC ring reuse — everything that moves
 * the union alpha, none of the paint (the fxContourOutline law, restated
 * over plain props because its own sig is module-private). Over-inclusion
 * is safe: a false mismatch only costs one raster-interim render. */
function silhouetteCheapSig(obj) {
  if (!obj) return '?';
  let stylesN = 0;
  try { stylesN = obj.styles ? JSON.stringify(obj.styles).length : 0; } catch (_) { stylesN = -1; }
  return [
    obj.type ?? '', obj.text ?? '', obj.fontFamily ?? '', obj.fontWeight ?? '',
    obj.fontStyle ?? '', obj.fontSize ?? '', obj.lineHeight ?? '',
    obj.charSpacing ?? '', obj.textAlign ?? '', obj.direction ?? '',
    Math.round(obj.width || 0), Math.round(obj.height || 0),
    obj.rx || 0, obj.ry || 0, obj.radius || 0,
    Array.isArray(obj.points) ? obj.points.length : 0,
    Array.isArray(obj.path) ? obj.path.length : (obj.path ? 1 : 0),
    obj.curveAmount || 0, obj.pathStartOffset || 0, obj.pathSide ?? '',
    stylesN,
  ].join('|');
}

/** Ring-shaping params key (u-scaled world, resolution-free except the
 * closing threshold flag, which mirrors the raster's device-px skip law). */
function ringParamsKey(p, u, closeOn, inkTag) {
  return [
    p.shape, p.border, p.cornerRadius, p.padWidth, p.padHeight,
    p.edgeStyle, p.edgeStyle === 'scallop' ? p.scallopSize : 0,
    u.toFixed(4), closeOn ? 1 : 0, inkTag || '',
  ].join('|');
}

/** Local pure bounds (exactOffset.boundsOfRings lives in the lazy geo chunk
 * — the boot law forbids importing it statically; sync paths need this). */
function ringsBoundsOf(rings) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const ring of rings || []) {
    for (const pt of ring) {
      const x = pt[0]; const y = pt[1];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return maxX > -Infinity ? { minX, minY, maxX, maxY } : null;
}

/** Plate rings → Path2D (object units). EvenOdd at fill time keeps counters
 * open no matter how the offset wound them (the house parity law). */
function ringsPathOf(rings) {
  const path = new Path2D();
  for (const ring of rings) {
    if (!ring || ring.length < 3) continue;
    path.moveTo(ring[0][0], ring[0][1]);
    for (let i = 1; i < ring.length; i++) path.lineTo(ring[i][0], ring[i][1]);
    path.closePath();
  }
  return path;
}

/** Polygonize an analytic badge geom into ONE exact ring. Chord sagitta ≤
 * chordU (callers pass 0.25 output px in object units — the tessellation
 * error law), so the ring is visually identical to the canvas arc. */
function badgeSourceRing(g, chordU) {
  const segsFor = (r) => {
    if (!(r > 0)) return 4;
    const eps = Math.min(r * 0.5, Math.max(1e-4, chordU));
    const theta = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - eps / r)));
    return Math.max(16, Math.min(512, Math.ceil((Math.PI * 2) / Math.max(1e-3, theta))));
  };
  if (g.kind === 'ellipse') {
    const n = segsFor(Math.max(g.rx, g.ry));
    const ring = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      ring.push([g.cx + Math.cos(a) * Math.max(0.01, g.rx), g.cy + Math.sin(a) * Math.max(0.01, g.ry)]);
    }
    return ring;
  }
  const rr = Math.max(0, Math.min(g.rr || 0, (g.x1 - g.x0) / 2, (g.y1 - g.y0) / 2));
  if (!(rr > 0.01)) {
    return [[g.x0, g.y0], [g.x1, g.y0], [g.x1, g.y1], [g.x0, g.y1]];
  }
  // Rounded rect: four quarter arcs (sagitta-bounded) + the straight runs.
  const nq = Math.max(4, Math.ceil(segsFor(rr) / 4));
  const ring = [];
  const corner = (cx, cy, a0) => {
    for (let i = 0; i <= nq; i++) {
      const a = a0 + (i / nq) * (Math.PI / 2);
      ring.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
    }
  };
  corner(g.x1 - rr, g.y0 + rr, -Math.PI / 2); // top-right
  corner(g.x1 - rr, g.y1 - rr, 0); // bottom-right
  corner(g.x0 + rr, g.y1 - rr, Math.PI / 2); // bottom-left
  corner(g.x0 + rr, g.y0 + rr, Math.PI); // top-left
  return ring;
}

// ---------------------------------------------------------------- textures
const TEX_CACHE = new Map(); // id|src → { img, ready, promise }

/** Accepts a texture FillValue ({id} manifest / {src} user-import — the
 * PaintPicker v2 split) or a bare manifest id string. Cache keys on
 * whichever the recipe carries. */
function textureEntry(fvOrId) {
  const id = typeof fvOrId === 'string' ? fvOrId : fvOrId?.id;
  const src = typeof fvOrId === 'object' ? fvOrId?.src : null;
  const key = id ?? src;
  let e = TEX_CACHE.get(key);
  if (e) return e;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  e = { img, ready: false, promise: null };
  e.promise = new Promise((resolve) => {
    img.onload = () => { e.ready = true; resolve(e); };
    img.onerror = () => { resolve(e); }; // stays !ready → solid fallback forever
    img.src = src ?? textureUrl(id);
  });
  TEX_CACHE.set(key, e);
  return e;
}

function solidOf(fv) {
  if (typeof fv === 'string') return fv;
  if (fv?.type === 'solid') return fv.color ?? '#ffffff';
  if (fv?.stops?.length) return fv.stops[0].color;
  return '#ffffff';
}

// ---------------------------------------------------------------- geometry
/** Badge geometry in OBJECT units from the measured/remembered ink rect.
 * kinds: 'ellipse' {cx,cy,rx,ry} (circle = equal radii) | 'rect'
 * {x0,y0,x1,y1,rr}. padWidth/padHeight grow it per-axis (square→rectangle,
 * circle→ellipse when the two differ). */
function badgeGeom(p, u, ink) {
  const cx = (ink.x0 + ink.x1) / 2;
  const cy = (ink.y0 + ink.y1) / 2;
  const spanX = Math.max(0.01, ink.x1 - ink.x0);
  const spanY = Math.max(0.01, ink.y1 - ink.y0);
  const padX = padU(p, u) + extraXU(p, u);
  const padY = padU(p, u) + extraYU(p, u);
  if (p.shape === 'circle') {
    // The ink box inscribed in a circle, stretched per-axis into an ELLIPSE
    // when Wider/Taller differ (the inscribing law extends per axis).
    const r0 = Math.hypot(spanX, spanY) / 2;
    return { kind: 'ellipse', cx, cy, rx: r0 + padX, ry: r0 + padY };
  }
  if (p.shape === 'square') {
    // Forced-square BASE (max dimension), then per-axis extras — so it stays
    // square only while Wider == Taller, honestly becoming a rectangle else.
    const base = Math.max(spanX, spanY) / 2;
    return {
      kind: 'rect',
      x0: cx - (base + padX), y0: cy - (base + padY),
      x1: cx + (base + padX), y1: cy + (base + padY), rr: 0,
    };
  }
  const x0 = ink.x0 - padX;
  const y0 = ink.y0 - padY;
  const x1 = ink.x1 + padX;
  const y1 = ink.y1 + padY;
  const rr = p.shape === 'rounded'
    ? Math.min(p.cornerRadius * u, (x1 - x0) / 2, (y1 - y0) / 2)
    : 0;
  return { kind: 'rect', x0, y0, x1, y1, rr };
}

/** Bounding rect of any geom (object units) — the fill anchor box. */
function geomBox(g) {
  if (g.kind === 'ellipse') {
    return { x0: g.cx - g.rx, y0: g.cy - g.ry, x1: g.cx + g.rx, y1: g.cy + g.ry };
  }
  return { x0: g.x0, y0: g.y0, x1: g.x1, y1: g.y1 };
}

/** Trace the badge path on a ctx whose transform maps OBJECT units. */
function traceBadge(ctx, g) {
  ctx.beginPath();
  if (g.kind === 'ellipse') {
    ctx.ellipse(g.cx, g.cy, Math.max(0.01, g.rx), Math.max(0.01, g.ry), 0, 0, Math.PI * 2);
    return;
  }
  const w = g.x1 - g.x0;
  const h = g.y1 - g.y0;
  if (g.rr > 0.01 && typeof ctx.roundRect === 'function') {
    ctx.roundRect(g.x0, g.y0, w, h, g.rr);
  } else if (g.rr > 0.01) {
    const r = g.rr;
    ctx.moveTo(g.x0 + r, g.y0);
    ctx.arcTo(g.x1, g.y0, g.x1, g.y1, r);
    ctx.arcTo(g.x1, g.y1, g.x0, g.y1, r);
    ctx.arcTo(g.x0, g.y1, g.x0, g.y0, r);
    ctx.arcTo(g.x0, g.y0, g.x1, g.y0, r);
    ctx.closePath();
  } else {
    ctx.rect(g.x0, g.y0, w, h);
  }
}

/** Set the badge fill on a ctx in OBJECT-unit space over the anchor box `box`.
 * Returns true when the paint is final; false when a texture is still loading
 * (placeholder used — the async upgrade repaints). */
function setBadgePaint(ctx, fv, box) {
  if (fv && fv.type === 'texture' && (fv.id || fv.src)) {
    const e = textureEntry(fv);
    if (!e.ready) { ctx.fillStyle = '#e8e8e8'; return false; }
    ctx.fillStyle = '#ffffff'; // under-paint: tiles with alpha stay a sticker
    return true; // caller pattern-fills after (needs save/scale)
  }
  const stops = fv?.stops;
  if ((fv?.type === 'linear-gradient' || fv?.type === 'radial-gradient') && stops?.length >= 2) {
    const { x0, y0, x1, y1 } = box;
    let grad;
    if (fv.type === 'radial-gradient') {
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;
      grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(x1 - x0, y1 - y0) / 2);
    } else {
      const a = ((fv.angle ?? 0) * Math.PI) / 180;
      const dx = Math.cos(a) / 2;
      const dy = Math.sin(a) / 2;
      const w = x1 - x0;
      const h = y1 - y0;
      grad = ctx.createLinearGradient(
        x0 + (0.5 - dx) * w, y0 + (0.5 - dy) * h,
        x0 + (0.5 + dx) * w, y0 + (0.5 + dy) * h,
      );
    }
    for (const s of stops) grad.addColorStop(clamp(s.offset, 0, 1, 0), s.color);
    ctx.fillStyle = grad;
    return true;
  }
  ctx.fillStyle = solidOf(fv);
  return true;
}

/** Pattern-fill the (already clipped) box with the texture tile at the 2D
 * pattern density, anchored at the box center so edits don't swim the tile. */
function patternFillBadge(ctx, fv, box) {
  const e = textureEntry(fv);
  if (!e.ready) return;
  const pat = ctx.createPattern(e.img, 'repeat');
  if (!pat) return;
  const k = (fv.scale ?? 1) * TEXTURE_PRINT_BASE_SCALE;
  const cx = (box.x0 + box.x1) / 2;
  const cy = (box.y0 + box.y1) / 2;
  const halfW = (box.x1 - box.x0) / 2;
  const halfH = (box.y1 - box.y0) / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(k, k);
  ctx.fillStyle = pat;
  ctx.fillRect(-(halfW / k) - 1, -(halfH / k) - 1, (halfW / k) * 2 + 2, (halfH / k) * 2 + 2);
  ctx.restore();
}

// ---------------------------------------------------------------- raster ops
/** 3-4 chamfer distance transform (device px × 3; orthogonal 3, diagonal 4) —
 * the fxExtrude rim primitive. `seed[i]` truthy = a source pixel. Two sweeps,
 * O(px). Used for both the match-text dilation and the plate-edge band. */
function chamfer34(seed, W, H) {
  const N = W * H;
  const INF = 1e9;
  const d = new Float32Array(N);
  for (let i = 0; i < N; i++) d[i] = seed[i] ? 0 : INF;
  for (let y = 0; y < H; y++) {
    const row = y * W;
    for (let x = 0; x < W; x++) {
      const i = row + x;
      let v = d[i];
      if (v === 0) continue;
      if (x > 0 && d[i - 1] + 3 < v) v = d[i - 1] + 3;
      if (y > 0) {
        const up = i - W;
        if (d[up] + 3 < v) v = d[up] + 3;
        if (x > 0 && d[up - 1] + 4 < v) v = d[up - 1] + 4;
        if (x < W - 1 && d[up + 1] + 4 < v) v = d[up + 1] + 4;
      }
      d[i] = v;
    }
  }
  for (let y = H - 1; y >= 0; y--) {
    const row = y * W;
    for (let x = W - 1; x >= 0; x--) {
      const i = row + x;
      let v = d[i];
      if (v === 0) continue;
      if (x < W - 1 && d[i + 1] + 3 < v) v = d[i + 1] + 3;
      if (y < H - 1) {
        const dn = i + W;
        if (d[dn] + 3 < v) v = d[dn] + 3;
        if (x < W - 1 && d[dn + 1] + 4 < v) v = d[dn + 1] + 4;
        if (x > 0 && d[dn - 1] + 4 < v) v = d[dn - 1] + 4;
      }
      d[i] = v;
    }
  }
  return d;
}

/** Stride-1 alpha (0-255) of a full-res canvas at working res W×H. */
function alphaAt(env, canvas, W, H) {
  const s = env.getScratch(W, H);
  s.ctx.setTransform(1, 0, 0, 1, 0, 0);
  s.ctx.clearRect(0, 0, W, H);
  s.ctx.drawImage(canvas, 0, 0, env.width, env.height, 0, 0, W, H);
  const d = s.ctx.getImageData(0, 0, W, H).data;
  const a = new Uint8ClampedArray(W * H);
  for (let i = 0; i < W * H; i++) a[i] = d[i * 4 + 3];
  return a;
}

/** Working-res copy of a full-res canvas region → {img, W, H, fx, scratch}. */
function workingCopy(env, canvas, resFactor) {
  const W0 = env.width;
  const H0 = env.height;
  const W = Math.max(8, Math.round(W0 * resFactor));
  const H = Math.max(8, Math.round(H0 * resFactor));
  if (W === W0 && H === H0 && canvas.getContext) {
    return { img: canvas.getContext('2d').getImageData(0, 0, W, H), W, H, fx: 1, scratch: null };
  }
  const s = env.getScratch(W, H);
  s.ctx.setTransform(1, 0, 0, 1, 0, 0);
  s.ctx.clearRect(0, 0, W, H);
  s.ctx.drawImage(canvas, 0, 0, W0, H0, 0, 0, W, H);
  return { img: s.ctx.getImageData(0, 0, W, H), W, H, fx: W / W0, scratch: s };
}

// ---------------------------------------------------------------- passes
/** The glyph-only variant canvas (stroke-suppressed base), or null. */
function variantCanvas(env) {
  if (typeof env.renderVariant !== 'function') return null;
  let v;
  try { v = env.renderVariant('noStroke'); } catch (_) { v = null; }
  return v && v.canvas ? v.canvas : null;
}

/** The glyph ink box in OBJECT units — measured from the variant on full
 * renders (order-proof: pure vector glyphs), remembered for region renders. */
function measureInk(env, resFactor) {
  const frame = env.frame;
  const obj = env.obj;
  if (!env.patchRegion) {
    const vc = variantCanvas(env);
    if (vc) {
      const W = Math.max(8, Math.round(env.width * resFactor));
      const H = Math.max(8, Math.round(env.height * resFactor));
      const a = alphaAt(env, vc, W, H);
      let px0 = W; let py0 = H; let px1 = -1; let py1 = -1;
      for (let y = 0; y < H; y++) {
        const row = y * W;
        for (let x = 0; x < W; x++) {
          if (a[row + x] >= 8) {
            if (x < px0) px0 = x;
            if (x > px1) px1 = x;
            if (y < py0) py0 = y;
            if (y > py1) py1 = y;
          }
        }
      }
      if (px1 >= px0 && py1 >= py0) {
        const kx = frame.effX * (W / env.width);
        const ky = frame.effY * (H / env.height);
        const ink = {
          x0: frame.x0 + px0 / kx,
          y0: frame.y0 + py0 / ky,
          x1: frame.x0 + (px1 + 1) / kx,
          y1: frame.y0 + (py1 + 1) / ky,
        };
        if (obj) memOf(obj).ink = ink;
        return ink;
      }
    }
    return obj ? MEM.get(obj)?.ink || null : null;
  }
  return obj ? MEM.get(obj)?.ink || null : null;
}

/** Put a stride-1 alpha into a fresh full-res scratch canvas (RGB = 0, alpha =
 * `a`), returned for masking/re-cut compositing. */
function alphaCanvas(env, a) {
  const W = env.width;
  const H = env.height;
  const s = env.getScratch(W, H);
  s.ctx.setTransform(1, 0, 0, 1, 0, 0);
  const img = s.ctx.createImageData(W, H);
  const d = img.data;
  for (let i = 0; i < W * H; i++) d[i * 4 + 3] = a[i];
  s.ctx.putImageData(img, 0, 0);
  return s.canvas;
}

/** Build the MATCH-TEXT plate silhouette at full res: dilate the glyph mask by
 * the margin, die-cut-close the pockets (the halo silhouette law). Returns a
 * full-res stride-1 alpha, or null. */
function buildMatchTextPlate(env, p) {
  const vc = variantCanvas(env);
  if (!vc) {
    logOnce('match-text plate skipped — seam offered no glyph variant');
    return null;
  }
  const W = env.width;
  const H = env.height;
  const glyph = alphaAt(env, vc, W, H);
  const seed = new Uint8Array(W * H);
  let any = false;
  for (let i = 0; i < W * H; i++) { if (glyph[i] >= 128) { seed[i] = 1; any = true; } }
  if (!any) return null;
  const radiusPx = Math.max(0, padU(p, env.u) * env.scale); // margin, device px
  const dist = chamfer34(seed, W, H); // ×3 device px
  const plate = new Uint8ClampedArray(W * H);
  const r3 = radiusPx * 3;
  for (let i = 0; i < W * H; i++) {
    const dv = dist[i];
    if (dv <= r3) plate[i] = 255;
    else if (dv <= r3 + 3) plate[i] = Math.round(255 * (1 - (dv - r3) / 3)); // 1px AA
  }
  // Die-cut closing on the UNION plate — bridge sub-margin pockets between
  // letters exactly as the baked border path does (reach = the dilation).
  const closeR = diecutCloseRadiusPx(radiusPx, env.u * env.scale);
  if (closeR > 0) {
    const img = new ImageData(W, H);
    for (let i = 0; i < W * H; i++) img.data[i * 4 + 3] = plate[i];
    try {
      bridgeDiecutPockets(img, W, H, closeR);
      for (let i = 0; i < W * H; i++) plate[i] = img.data[i * 4 + 3];
    } catch (e) { logOnce('plate die-cut closing failed — pocketed plate', e); }
  }
  return plate;
}

/** Paint the plate edge band + dome into the (already filled) plate on S, then
 * re-cut the crisp die-cut edge. Runs only when edge or bevel is active. */
function finishPlate(S, env, p, resFactor, recut) {
  if (!p.edge && !p.beveled) return;
  const obj = env.obj;
  const work = workingCopy(env, S.canvas, resFactor);
  const { W, H } = work;
  const wd = work.img.data;
  const mask = new Uint8ClampedArray(W * H);
  for (let i = 0; i < W * H; i++) mask[i] = wd[i * 4 + 3];
  const unitPx = Math.max(0.02, env.u * env.scale * work.fx);

  // EDGE contour: an inside-aligned band hugging the plate's own outer edge
  // (distance to the plate BACKGROUND ≤ the edge width). Painted before the
  // dome so a beveled plate domes fill + edge together.
  if (p.edge) {
    const bg = new Uint8Array(W * H);
    for (let i = 0; i < W * H; i++) bg[i] = mask[i] >= 128 ? 0 : 1;
    const din = chamfer34(bg, W, H); // ×3 device px, inside distance
    const bandPx3 = Math.max(1, p.edge.width * 0.2 * unitPx) * 3;
    const [er, eg, eb] = hexRGB(p.edge.color);
    for (let i = 0; i < W * H; i++) {
      if (mask[i] === 0) continue;
      if (din[i] <= bandPx3) { const o = i * 4; wd[o] = er; wd[o + 1] = eg; wd[o + 2] = eb; }
    }
  }

  // DOME the plate's OWN alpha directly — a filled plate has only outer edges,
  // so rims rise at the die-cut edge, flat where it meets the letters.
  if (p.beveled) {
    const dome = { clampPx: 0, measuredPx: 0 };
    if (env.patchRegion && obj) {
      const m = MEM.get(obj);
      if (m && m.plateDistU > 0) dome.clampPx = m.plateDistU * unitPx;
    }
    shadeBevelCore(work.img, null, W, H, domeParams(p), unitPx, LIGHT, dome,
      { mask, under: 'none' });
    if (!env.patchRegion && obj && dome.measuredPx > 0) {
      memOf(obj).plateDistU = dome.measuredPx / unitPx;
    }
  }

  if (work.fx === 1) {
    S.ctx.putImageData(work.img, 0, 0);
  } else {
    work.scratch.ctx.putImageData(work.img, 0, 0);
    S.ctx.save();
    S.ctx.setTransform(1, 0, 0, 1, 0, 0);
    S.ctx.clearRect(0, 0, env.width, env.height);
    // Soft for a beat at half res — the async pass replaces it (house law).
    S.ctx.drawImage(work.scratch.canvas, 0, 0, W, H, 0, 0, env.width, env.height);
    // Re-cut the analytic/silhouette edge crisp at full res (the upscale
    // softened it; a die-cut edge must stay sharp, shading inside stays smooth).
    S.ctx.globalCompositeOperation = 'destination-in';
    recut(S.ctx);
    S.ctx.restore();
  }
}

// ------------------------------------------------- v3 async builders
/**
 * Build (or reuse) the FINAL plate rings for this render — the exact-vector
 * path. Returns { rings, bounds } or null (raster fallback). Wasm rides the
 * lazy geo chunk; every failure degrades silently to the raster path (the
 * caller logs once).
 *
 * Match-text: source contours = the union silhouette (traceObjectSilhouette
 * — text via the seam's 'filledSolid' variant, shapes/icons via the U1
 * silhouette variant; region bakes reuse remembered contours). The margin
 * offset (round join) IS the dilation; the ring-space closing mirrors the
 * raster diecutClose radius law bit-for-spirit (min(margin·0.4, 6u),
 * skipped when sub-1.5 device px at this render's scale).
 * Badges: the analytic geometry polygonized (margin already inside
 * badgeGeom) — rings are only needed for scallop/puffy, and the smooth flat
 * badge deliberately keeps its analytic canvas path byte-identical.
 */
async function plateRingsFor(env, p, ink) {
  const obj = env.obj;
  const u = env.u;
  const m = memOf(obj);
  const marginU = padU(p, u);
  const closeU = Math.min(marginU * 0.4, 6 * u);
  const closeOn = p.shape === 'outline' && closeU * env.scale >= 1.5;

  if (p.shape === 'outline') {
    // Contour source per kind (the v3 routing): SHAPES cut EXACT VECTORS —
    // lib/geo/shapeRings (N2's 2D twin of the silhouette law; lazy import
    // under the boot law) mirrors fabric's own _render stream, so a circle
    // plate is chord-exact at the tessellation budget with no raster
    // round-trip. TEXT and ICONS ride the traced union contours (the
    // sub-pixel tracer, cached per object). Either source feeds the same
    // offset pipeline below; a failed vector cut falls to the tracer.
    const kind = objectKindOf(obj);
    let srcTag = '';
    if (kind === 'shape') {
      const q = Math.ceil(Math.log2(Math.max(0.02, env.scale))); // tol bucket
      srcTag = `vec:${silhouetteCheapSig(obj)}:q${q}`;
    }
    const pkey = ringParamsKey(p, u, closeOn, srcTag);
    const R = m.rings;
    // Vector-source reuse rides pkey alone (srcTag carries the geometry
    // sig + tol bucket); traced-source reuse additionally pins the contour
    // array IDENTITY (the tracer memoizes, so identity = same silhouette
    // at the same tier).
    if (srcTag && R && R.pkey === pkey && R.rings) return R;
    let contours = null;
    if (srcTag) {
      try {
        const sr = await import('../geo/shapeRings.js');
        const vec = sr.ringsOfObjectLocal(obj, { tol: 0.25 / Math.max(0.02, env.scale) });
        if (vec && vec.length) contours = vec;
      } catch (e) {
        logOnce('shapeRings unavailable — traced contours for the shape plate', e);
      }
    }
    if (!contours) {
      contours = traceObjectSilhouette(env);
      if (!contours || !contours.length) return null;
      if (!srcTag && R && R.pkey === pkey && R.src === contours && R.rings) return R;
    }
    const geo = await import('../geo/exactOffset.js');
    let rings = await geo.offsetRings(contours, marginU, { join: 'round' });
    if (closeOn && rings.length) {
      rings = await geo.offsetRings(rings, closeU, { join: 'round' });
      rings = await geo.offsetRings(rings, -closeU, { join: 'round' });
    }
    if (p.edgeStyle === 'scallop' && rings.length) {
      const r = scallopRadiusU(p, u);
      rings = await geo.scallopEdge(rings, r, r * SCALLOP_SPACING_K, { direction: 'out' });
      stats.scallopPlates += 1;
    }
    if (!rings.length) return null;
    stats.ringBuilds += 1;
    stats.lastRingCount = rings.length;
    const out = {
      pkey, sig: silhouetteCheapSig(obj), src: contours, rings, bounds: ringsBoundsOf(rings),
    };
    m.rings = out;
    return out;
  }

  // Analytic badge → one exact ring (+ scallop). Ink box is the geometry
  // anchor, so it rides the key (a reflow moves the badge, not the params).
  if (!ink) return null;
  const inkTag = `${ink.x0.toFixed(2)},${ink.y0.toFixed(2)},${ink.x1.toFixed(2)},${ink.y1.toFixed(2)}`;
  const pkey = ringParamsKey(p, u, false, inkTag);
  const R = m.rings;
  if (R && R.pkey === pkey && R.rings) return R;
  const g = badgeGeom(p, u, ink);
  let rings = [badgeSourceRing(g, 0.25 / Math.max(0.02, env.scale))];
  if (p.edgeStyle === 'scallop') {
    const geo = await import('../geo/exactOffset.js');
    const r = scallopRadiusU(p, u);
    rings = await geo.scallopEdge(rings, r, r * SCALLOP_SPACING_K, { direction: 'out' });
    stats.scallopPlates += 1;
  }
  if (!rings.length) return null;
  stats.ringBuilds += 1;
  stats.lastRingCount = rings.length;
  const out = {
    pkey, sig: silhouetteCheapSig(obj), src: null, rings, bounds: ringsBoundsOf(rings),
  };
  m.rings = out;
  return out;
}

/** Puffy-mesh key: the rings' identity (pkey covers params+u; bounds pin
 * the badge reflow case) + the puff params. */
function puffyMeshKeyOf(ringsInfo, p) {
  const b = ringsInfo.bounds;
  return [
    ringsInfo.pkey, p.puffDepth, p.bodyMaterial || '',
    b ? `${b.minX.toFixed(2)},${b.minY.toFixed(2)},${b.maxX.toFixed(2)},${b.maxY.toFixed(2)}` : '',
  ].join('|');
}

/**
 * Bake the puffy slab for this render — the one-shot 3D path (see header).
 * Returns { canvas, rect } (rect in object units — the ortho camera's exact
 * bounds), { none: true } when the plate lies fully outside a patch slice,
 * or null (degrade → flat dome interim). three/renderer3d load lazily here
 * only — never at boot.
 */
async function bakePuffyPlate(env, p, ringsInfo) {
  const obj = env.obj;
  const u = env.u;
  const b = ringsInfo.bounds;
  if (!b) return null;
  // Plate rect (+AA slack), clipped to a patch slice when region-baking —
  // the ortho camera makes the slice an exact crop of the full bake.
  let rect = {
    x0: b.minX - u, y0: b.minY - u, x1: b.maxX + u, y1: b.maxY + u,
  };
  if (env.patchRegion) {
    const r = env.patchRegion;
    const pad = 2 / Math.max(0.02, env.scale);
    rect = {
      x0: Math.max(rect.x0, r.x0 - pad),
      y0: Math.max(rect.y0, r.y0 - pad),
      x1: Math.min(rect.x1, r.x1 + pad),
      y1: Math.min(rect.y1, r.y1 + pad),
    };
    if (!(rect.x1 - rect.x0 > 1e-3) || !(rect.y1 - rect.y0 > 1e-3)) return { none: true };
  }

  // ---- the solid (memoized typed arrays — the expensive half) ----
  const m = memOf(obj);
  const H = puffHeightU(p, u);
  const meshKey = puffyMeshKeyOf(ringsInfo, p);
  let mesh = m.puffyMesh && m.puffyMesh.key === meshKey ? m.puffyMesh.mesh : null;
  if (!mesh) {
    const geoPuffy = await import('../geo/puffySolid.js');
    // World = object x, NEGATED object y (three is y-up), z out of the
    // poster. flatBack rests on z=0 — the poster surface.
    const flipped = ringsInfo.rings.map((ring) => ring.map(([x, y]) => [x, -y]));
    mesh = await geoPuffy.buildPuffySolid(flipped, {
      height: H,
      edgeRound: H * PUFF_EDGE_ROUND_K,
      flatBack: true,
      engine: 'auto',
      // Grid density rides the delivered resolution (tessellation law).
      pxPerWorld: Math.max(0.02, env.scale),
    });
    if (!mesh) return null;
    if (mesh.stats && mesh.stats.degraded === 'extrude') {
      logOnce('puffy inset lost ink even at the guard floor — sharp extrude body (honest degrade)');
    }
    stats.lastPuffyEngine = (mesh.stats && mesh.stats.engine) || null;
    m.puffyMesh = { key: meshKey, mesh };
  }

  // ---- output size under this render's budget ----
  const effX = env.frame.effX;
  const effY = env.frame.effY;
  let W = Math.max(8, Math.round((rect.x1 - rect.x0) * effX));
  let Hpx = Math.max(8, Math.round((rect.y1 - rect.y0) * effY));
  const bounds = env.renderBounds || { side: 4096, area: 4096 * 4096 };
  let k = Math.min(1, bounds.side / Math.max(W, Hpx));
  if (W * k * (Hpx * k) > bounds.area) k *= Math.sqrt(bounds.area / (W * k * Hpx * k));
  if (k < 1) {
    W = Math.max(8, Math.round(W * k));
    Hpx = Math.max(8, Math.round(Hpx * k));
  }

  // ---- minimal one-shot scene (mirrors the extrude source bake's shape:
  // build → bakeScene → dispose everything local; renderer3d owns the GL) ----
  const three = await import('three');
  const { toBufferGeometry } = await import('../geo/meshArrays.js');
  const { bakeScene } = await import('../text3d/renderer3d.js');
  const geometry = await toBufferGeometry(mesh, three);
  const fv = p.fill;
  const isTexture = !!(fv && fv.type === 'texture' && (fv.id || fv.src));
  let map = null;
  if (isTexture) {
    const e = textureEntry(fv);
    if (!e.ready) { try { await e.promise; } catch (_) { /* solid fallback */ } }
    if (e.ready) {
      // Planar top-projection UVs at the 2D pattern density, anchored at the
      // PLATE-BOUNDS center — the same tile scale + anchor law as the flat
      // pattern fill, so flat↔puffy keeps the texture registration. The
      // walls stretch the edge texels (die-cut stock look); rims are small.
      const kTile = (fv.scale ?? 1) * TEXTURE_PRINT_BASE_SCALE;
      const tw = Math.max(1e-3, e.img.width * kTile);
      const th = Math.max(1e-3, e.img.height * kTile);
      const cx = (b.minX + b.maxX) / 2;
      const cy = (b.minY + b.maxY) / 2;
      const pos = geometry.getAttribute('position');
      const uv = new Float32Array(pos.count * 2);
      for (let i = 0; i < pos.count; i++) {
        const xw = pos.getX(i);
        const yObj = -pos.getY(i);
        uv[i * 2] = 0.5 + (xw - cx) / tw;
        uv[i * 2 + 1] = 0.5 + (yObj - cy) / th;
      }
      geometry.setAttribute('uv', new three.BufferAttribute(uv, 2));
      map = new three.Texture(e.img);
      map.wrapS = three.RepeatWrapping;
      map.wrapT = three.RepeatWrapping;
      map.colorSpace = three.SRGBColorSpace;
      map.flipY = false;
      map.anisotropy = 4;
      map.needsUpdate = true;
    }
  }
  // Basic body material (volumeMaterials presets arrive in a later lane —
  // p.bodyMaterial is stored but not yet resolved). Gradients fall to their
  // first stop (documented in the header).
  const mat = new three.MeshStandardMaterial({
    color: map ? '#ffffff' : solidOf(fv),
    map,
    roughness: 0.45,
    metalness: 0,
  });
  const scene = new three.Scene();
  scene.add(new three.Mesh(geometry, mat));
  // The house sticker sun: az 135 (top-left), the hidden bevel altitude —
  // flat-dome and puffy stickers read as one light family. World y is up
  // (= −canvas y), so the from-direction is (cos az, sin az, sin alt)·…
  const az = (LIGHT.azDeg * Math.PI) / 180;
  const alt = (LIGHT.altDeg * Math.PI) / 180;
  const key = new three.DirectionalLight(0xffffff, 1.9);
  key.position.set(
    Math.cos(az) * Math.cos(alt),
    Math.sin(az) * Math.cos(alt),
    Math.sin(alt),
  ).normalize();
  scene.add(key);
  scene.add(new three.AmbientLight(0xffffff, 0.55));
  const fill = new three.DirectionalLight(0xffffff, 0.35);
  fill.position.set(0.4, -0.3, 0.8).normalize();
  scene.add(fill);
  // Straight-on ortho over the plate rect (world y = −object y).
  const camera = new three.OrthographicCamera(rect.x0, rect.x1, -rect.y0, -rect.y1, 1, H + 40);
  camera.position.set(0, 0, H + 20);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);
  const t0 = (globalThis.performance ? performance.now() : Date.now());
  try {
    const canvas = await bakeScene({
      scene,
      camera,
      width: W,
      height: Hpx,
      ssaa: 2,
      shadows: false,
      triBudget: capsTriBudget(env.exporting ? 'export' : 'live'),
    });
    stats.puffyBakes += 1;
    stats.lastPuffyMs = (globalThis.performance ? performance.now() : Date.now()) - t0;
    return { canvas, rect };
  } finally {
    geometry.dispose();
    mat.dispose();
    if (map) map.dispose();
    scene.clear();
  }
}

/**
 * The applyAsync pre-pass: rings (exact path) + the puffy bake, all guarded
 * — null means "today's raster/analytic path exactly" (plus the flat-dome
 * stand-in when body is puffy). Also measures ink ONCE and hands it down so
 * drawPlate doesn't re-read the variant alpha.
 */
async function buildPlateExtras(env, p) {
  if (!env.frame || !env.obj) return null;
  const wantRings = p.shape === 'outline' || p.edgeStyle === 'scallop' || p.body === 'puffy';
  if (!wantRings) return null; // smooth flat badge: analytic path, byte-identical
  const ink = measureInk(env, 1);
  if (p.shape !== 'outline' && !ink) return { ink };
  let ringsInfo = null;
  try {
    ringsInfo = await plateRingsFor(env, p, ink);
  } catch (e) {
    logOnce('exact plate rings failed — raster dilation fallback', e);
  }
  if (!ringsInfo) {
    stats.rasterFallbacks += 1;
    if (p.body === 'puffy') stats.puffyDegrades += 1; // no rings → no solid
    return { ink };
  }
  stats.exactPlates += 1;
  let puffy = null;
  if (p.body === 'puffy') {
    if (!puffyBakeWanted(p)) {
      stats.puffyDegrades += 1;
      logOnce('puffy sticker degrades to the flat dome — renderCaps materialTier floor');
    } else if (env.obj.isEditing && !env.exporting) {
      // Belt only — the seam's WebGL fence (stickerWantsGlBake) already
      // holds async upgrades mid-edit. Flat dome interim.
    } else {
      try {
        puffy = await bakePuffyPlate(env, p, ringsInfo);
      } catch (e) {
        logOnce('puffy plate bake failed — flat dome interim', e);
      }
      if (!puffy) stats.puffyDegrades += 1;
    }
  }
  return { ink, rings: ringsInfo, puffy };
}

/** Draw the plate under the letters (every mode).
 * v3: `extras` (async pre-pass, see buildPlateExtras) may carry the exact
 * plate RINGS and/or the baked PUFFY slab; null = the pre-lane raster/
 * analytic paths byte-for-byte (with the flat-dome stand-in when body is
 * puffy — the interim/degrade law). */
function drawPlate(src, env, p, resFactor, extras = null) {
  const frame = env.frame;
  if (!frame) { logOnce('plate skipped — env.frame missing (pre-lane seam?)'); return src; }

  const ink = (extras && extras.ink) || measureInk(env, resFactor);
  const S = env.getScratch(env.width, env.height);
  S.ctx.setTransform(1, 0, 0, 1, 0, 0);
  S.ctx.clearRect(0, 0, env.width, env.height);

  const setFrame = (ctx) => ctx.setTransform(
    frame.effX, 0, 0, frame.effY, -frame.x0 * frame.effX, -frame.y0 * frame.effY,
  );
  const isTexture = !!(p.fill && p.fill.type === 'texture' && (p.fill.id || p.fill.src));
  const puffyPix = extras && extras.puffy && extras.puffy.canvas ? extras.puffy : null;
  const puffyOff = !!(extras && extras.puffy && extras.puffy.none);
  const ringsInfo = extras && extras.rings && extras.rings.rings ? extras.rings : null;
  // Flat-dome STAND-IN: body puffy without a slab this render (sync interim,
  // floor tier, bake hiccup) shades the flat plate with the dome — the
  // closest 2D read and the documented floor-tier look. Never for a plate
  // that is simply outside the patch slice.
  const pd = (p.body === 'puffy' && !puffyPix && !puffyOff) ? { ...p, beveled: true } : p;

  if (puffyPix) {
    // PUFFY SLAB — the 3D bake replaces fill + finish + dome wholesale
    // (registration: the ortho camera's rect in object units, one drawImage
    // under the frame transform). The optional edge band still paints — it
    // follows the painted alpha, i.e. the slab's own die-cut silhouette.
    const r = puffyPix.rect;
    S.ctx.save();
    setFrame(S.ctx);
    S.ctx.drawImage(puffyPix.canvas, r.x0, r.y0, r.x1 - r.x0, r.y1 - r.y0);
    S.ctx.restore();
    if (p.edge) {
      const path = ringsInfo ? ringsPathOf(ringsInfo.rings) : null;
      finishPlate(S, env, { ...p, beveled: false }, resFactor, (ctx) => {
        if (path) { setFrame(ctx); ctx.fill(path, 'evenodd'); }
      });
    }
  } else if (puffyOff) {
    // Plate fully outside this patch slice — letters only.
  } else if (ringsInfo) {
    // EXACT plate (v3): the final rings (margin offset + closing + scallop)
    // fill under the EvenOdd parity law; the existing fill/finish/dome
    // pipeline runs unchanged on the raster — only the contour source
    // upgraded. Fill anchor box = the rings' own bounds (≡ ink+margin for
    // smooth plates to sub-pixel; grows honestly with scallop bumps).
    const path = ringsPathOf(ringsInfo.rings);
    const bb = ringsInfo.bounds;
    const box = { x0: bb.minX, y0: bb.minY, x1: bb.maxX, y1: bb.maxY };
    S.ctx.save();
    setFrame(S.ctx);
    const final = setBadgePaint(S.ctx, pd.fill, box);
    S.ctx.fill(path, 'evenodd');
    if (isTexture && final) {
      S.ctx.clip(path, 'evenodd');
      patternFillBadge(S.ctx, pd.fill, box);
    }
    S.ctx.restore();
    finishPlate(S, env, pd, resFactor, (ctx) => { setFrame(ctx); ctx.fill(path, 'evenodd'); });
  } else if (p.shape === 'outline') {
    // MATCH TEXT (raster fallback): the dilated, die-cut-closed glyph
    // silhouette, filled — the documented auto fallback (no contours / no
    // wasm / sync interim).
    const plate = buildMatchTextPlate(env, pd);
    if (!plate) return src; // empty text / no variant — nothing to back
    if (!ink) return src;
    const box = { x0: ink.x0 - padU(pd, env.u), y0: ink.y0 - padU(pd, env.u),
      x1: ink.x1 + padU(pd, env.u), y1: ink.y1 + padU(pd, env.u) };
    // Paint the fill across the plate box, then die-cut to the silhouette.
    S.ctx.save();
    setFrame(S.ctx);
    const final = setBadgePaint(S.ctx, pd.fill, box);
    S.ctx.fillRect(box.x0, box.y0, box.x1 - box.x0, box.y1 - box.y0);
    if (isTexture && final) {
      S.ctx.beginPath();
      S.ctx.rect(box.x0, box.y0, box.x1 - box.x0, box.y1 - box.y0);
      S.ctx.clip();
      patternFillBadge(S.ctx, pd.fill, box);
    }
    S.ctx.restore();
    const maskCv = alphaCanvas(env, plate);
    S.ctx.save();
    S.ctx.setTransform(1, 0, 0, 1, 0, 0);
    S.ctx.globalCompositeOperation = 'destination-in';
    S.ctx.drawImage(maskCv, 0, 0, env.width, env.height, 0, 0, env.width, env.height);
    S.ctx.restore();
    finishPlate(S, env, pd, resFactor, (ctx) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(maskCv, 0, 0, env.width, env.height, 0, 0, env.width, env.height);
    });
  } else {
    // ANALYTIC badge (circle/ellipse/rect/square/rounded) — byte-identical
    // pre-lane path for smooth flat badges.
    if (!ink) {
      if (env.patchRegion) logOnce('badge skipped in a region bake — no remembered ink box yet');
      return src;
    }
    const g = badgeGeom(pd, env.u, ink);
    const box = geomBox(g);
    S.ctx.save();
    setFrame(S.ctx);
    const final = setBadgePaint(S.ctx, pd.fill, box);
    traceBadge(S.ctx, g);
    S.ctx.fill();
    if (isTexture && final) {
      traceBadge(S.ctx, g);
      S.ctx.clip();
      patternFillBadge(S.ctx, pd.fill, box);
    }
    S.ctx.restore();
    finishPlate(S, env, pd, resFactor, (ctx) => { setFrame(ctx); traceBadge(ctx, g); ctx.fill(); });
  }

  // Letters over the plate — the incoming raster keeps everything the chain
  // already did (bevel shading, its contact shadow, which now lands ON the
  // plate). The base render suppressed the sticker ring, so `src` is the
  // letters layer alone — no double-paint.
  S.ctx.save();
  S.ctx.setTransform(1, 0, 0, 1, 0, 0);
  S.ctx.drawImage(src, 0, 0, env.width, env.height, 0, 0, env.width, env.height);
  S.ctx.restore();
  return S.canvas;
}

async function ensureTextureLoaded(p) {
  if (p.fill && p.fill.type === 'texture' && (p.fill.id || p.fill.src)) {
    try { await textureEntry(p.fill).promise; } catch (_) { /* solid fallback */ }
  }
}

registerFx({
  id: 'stickerShape',
  // v2 (plate-model lane, owner 2026-07-20): the sticker is a SOLID PLATE under
  // the letters in EVERY mode (match-text = the dilated die-cut glyph
  // silhouette; shapes = analytic badges) — never a glyph-hugging ring. The
  // bevel domes the plate's own alpha (outer edges only); `finish` selects the
  // dome surface; `padWidth`/`padHeight` grow analytic badges per-axis
  // (square→rectangle, circle→ellipse); optional `edge` paints the plate's
  // border contour. v1 recipes migrate to plates (owner-ruled the correct look).
  // v3 (Depth-Everywhere N1, 2026-07-22): exact-vector plate contours where
  // contours exist (raster dilation kept as the auto fallback — margin law
  // now sub-pixel), edgeStyle 'scallop' (exactOffset.scallopEdge, count law
  // n = perimeter/spacing), body 'puffy' (real 3D slab via puffySolid +
  // the one-shot renderer; beveled+puffy exclusive — sanitize precedence;
  // floor tier degrades to the flat dome). Old params render the same look
  // (smooth match-text plates gain sub-pixel edge exactness — sanctioned).
  version: 3,
  kind: 'filter',
  sanitize,
  isEmpty,
  spill,
  spillXY,
  // Export/warm-up asset hook (cold-export texture lane): applySync paints a
  // NEUTRAL PLACEHOLDER while a texture tile decodes (setBadgePaint → false) —
  // invisible on screen (the async upgrade repaints) but a COLD export's sync
  // render is the deliverable with no upgrade to follow, so it would ship the
  // placeholder. Return the pending tile-decode promise so the export bracket
  // (fxRender fxPrimeAssets) can await it BEFORE the render paints. Calling
  // textureEntry here also KICKS OFF the decode for a plate never yet rendered
  // (truly cold). Null = nothing to await (solid/gradient fill, or the tile is
  // already decoded — zero overhead). `p` is already sanitized by the caller.
  primeAssets: (p) => {
    const fv = p && p.fill;
    if (fv && fv.type === 'texture' && (fv.id || fv.src)) {
      const e = textureEntry(fv);
      if (!e.ready) return e.promise;
    }
    return null;
  },
  applySync: (canvas, env) => {
    const p = env.params;
    if (isEmpty(p)) return canvas;
    const resFactor = env.quality === 'full' ? 1 : 0.5;
    // v3 sync law: no wasm, no WebGL here (per-keystroke path). Remembered
    // rings redraw the exact plate when the silhouette + params still match
    // (Path2D is cheap); anything else paints the raster/analytic interim
    // and the debounced async pass lands the exact/puffy result. Puffy is
    // NEVER baked here — the flat dome stands in (drawPlate's pd law).
    let extras = null;
    const wantRings = p.shape === 'outline' || p.edgeStyle === 'scallop' || p.body === 'puffy';
    if (wantRings && env.obj) {
      const m = MEM.get(env.obj);
      const R = m && m.rings;
      if (R && R.rings && R.sig === silhouetteCheapSig(env.obj)) {
        // pkey pins the ring-shaping params; the badge ink tag inside it
        // re-validates against the CURRENT remembered ink automatically
        // (measureInk keeps MEM.ink fresh on full renders).
        const u = env.u;
        const marginU = padU(p, u);
        const closeOn = p.shape === 'outline'
          && Math.min(marginU * 0.4, 6 * u) * env.scale >= 1.5;
        const inkTag = p.shape !== 'outline' && m.ink
          ? `${m.ink.x0.toFixed(2)},${m.ink.y0.toFixed(2)},${m.ink.x1.toFixed(2)},${m.ink.y1.toFixed(2)}`
          : '';
        if (R.pkey === ringParamsKey(p, u, closeOn, inkTag)) extras = { rings: R };
      }
    }
    return drawPlate(canvas, env, p, resFactor, extras);
  },
  applyAsync: async (canvas, env) => {
    const p = env.params;
    if (isEmpty(p)) return canvas;
    await ensureTextureLoaded(p);
    // v3: exact rings + (optionally) the puffy slab bake — all guarded, all
    // degrading to the pre-lane raster paths. See buildPlateExtras.
    const extras = await buildPlateExtras(env, p);
    return drawPlate(canvas, env, p, 1, extras);
  },
});
